//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'

import { useOtChainState, useOtSelector } from '@react/redux/store'

import { EthAddr } from '@shared/SchemaTypes'

import { Box, Typography } from '@mui/material'
import { PoolEventCTO } from '@shared/sharedTypes'

import { GridSortModel } from '@mui/x-data-grid-premium'

import { ReportDataShape } from '@shared/ReportDefinitionCTO'
import { logOT } from '@shared/loggingShared'
import { fetchForAccountAddrPoolEventCTOs, fetchPoolEventCTOs } from '@react/fetchers/PoolFetchers'
import ReportExplorer from '@react/reporting/ReportExplorer'

export default function TransactionList(props: {
  lenderAddr?: EthAddr
  poolAddr?: EthAddr
  numberOfDays?: number
}): ReactElement {
  const [loading, setLoading] = useState<boolean>(false)
  const [fetchDataError, setFetchDataError] = useState<any | undefined>(undefined)

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { accountAddr },
    chainConfiguration: { chainConfigurationName, providerReadOnly },
  } = useOtChainState()

  const { isInitialized, poolOverviewCTOs } = useOtSelector((state) => state.poolState)

  const [rows, setRows] = React.useState<PoolEventCTO[]>([])

  const lenderAddr: EthAddr = props.lenderAddr ?? accountAddr ?? ''
  const numberOfDays = props.numberOfDays
  const poolAddr = props.poolAddr

  useEffect(() => {
    async function loadTransactionEvents() {
      try {
        setFetchDataError(undefined)
        if (!providerReadOnly) return
        setLoading(true)

        if (chainConfigurationName == 'Hardhat') {
          const poolAddr = '0xAb05a5dF41F0d920AD441732aE38Aca37832C35C'
          await fetchPoolEventCTOs(serverApiBaseUrl, restAPIKey, poolAddr, lenderAddr)
        }

        if (accountAddr && poolOverviewCTOs && isInitialized) {
          let startDayStr = ''
          if (numberOfDays) {
            const currentDate = new Date()
            const startDate = new Date(currentDate.setUTCDate(currentDate.getUTCDate() - numberOfDays))
            startDayStr = `${startDate.getUTCFullYear()}-${(startDate.getUTCMonth() + 1)
              .toString()
              .padStart(2, '0')}-${startDate.getUTCDate().toString().padStart(2, '0')}`
          }

          const allEvents = await fetchForAccountAddrPoolEventCTOs(
            serverApiBaseUrl,
            restAPIKey,
            lenderAddr,
            startDayStr
          )

          // Filter events by poolAddr if provided
          const filteredEvents = poolAddr ? allEvents.filter((event) => event.poolAddr === poolAddr) : allEvents

          setRows(filteredEvents)
          setLoading(false)
        }
      } catch (error: any) {
        logOT.error('Error fetching pool events', error)
        setFetchDataError(error)
        setLoading(false)
      }
    }
    loadTransactionEvents()
  }, [providerReadOnly, isInitialized, accountAddr, numberOfDays, poolAddr])

  if (!loading && rows.length === 0) {
    return (
      <Box sx={{ marginTop: '4rem' }}>
        <Typography variant="h4" align="center">
          You do not have any transactions yet
        </Typography>
      </Box>
    )
  }

  const defaultColumnNames = ['transactionHash', 'eventName', 'timestamp', 'assets', 'shares', 'poolAddr']
  const defaultSortModel: GridSortModel = [{ field: 'timestamp', sort: 'desc' }]
  return (
    <>
      <ReportExplorer
        loading={loading}
        fetchDataError={fetchDataError}
        defaultColumnNames={defaultColumnNames}
        defaultSortModel={defaultSortModel}
        reportDataShape={ReportDataShape.POOL_EVENTS}
        rows={rows}
      />
    </>
  )
}
